import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Modal,
  Table,
  Text,
  toast,
} from '@workwhile/ui'
import { User } from 'api/typings'
import type { UserProfile } from 'api/user/profile'
import {
  ListDocument,
  uploadI9DocumentRequest,
} from 'api/user/uploadI9Document'
import {
  useSubmitSupplementBMutation,
  useUploadI9DocumentMutation,
} from 'queries/user'
import { userKey } from 'queries/user/keys'
import React, { useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { z } from 'zod'
import { DocumentTitles } from './constants'
import { DocumentUpload } from './DocumentUpload'
import { SupplementBForm, type SupplementBFormHandle } from './SupplementBForm'

type FormData = z.infer<typeof uploadI9DocumentRequest>

type I9UploadUser = User | UserProfile

const COLUMNS = [
  {
    header: 'Field',
    accessorKey: 'label',
    cell: ({ getValue }) => <Text color="lightText">{getValue()}</Text>,
  },
  {
    header: 'Value',
    accessorKey: 'value',
    cell: ({ getValue }) => <Text color="text">{getValue()}</Text>,
  },
]

const transformData = (document: ListDocument) => {
  return [
    ['List Type', document.listType],
    ['Document Type', DocumentTitles[document.documentType]],
    ['Document Number', document.documentNumber],
    ['Issuing Authority', document.issuingAuthority],
    ['Expiration Date', document.expirationDate],
    ['Date of Birth', document.dateOfBirth],
    ['Name on Document', document.name],
  ]
    .filter(([, value]) => !!value)
    .map(([label, value]) => ({ label, value }))
}

export const I9DocumentUpload = ({
  user,
  onUploadSuccess,
  layout = 'modal',
}: {
  user: I9UploadUser
  onUploadSuccess?: () => void
  layout?: 'modal' | 'inline'
}) => {
  const [error, setError] = useState<string | null>('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const supplementBRef = useRef<SupplementBFormHandle>(null)
  const queryClient = useQueryClient()
  const userId = String(user.id)

  const form = useForm<FormData>({
    resolver: zodResolver(uploadI9DocumentRequest),
    defaultValues: {
      documents: [],
      isSupplementB: false,
    },
    mode: 'onChange',
  })

  const { mutate: submitSupplementB, isPending: isSubmitSupplementBPending } =
    useSubmitSupplementBMutation(userId, {
      onSuccess: () => {
        toast.success('Supplement B submitted successfully!', {
          position: 'top-center',
        })
        supplementBRef.current?.reset()
        onUploadSuccess?.()
      },
      onError: (err) => {
        console.error('Supplement B submission error:', err)
        setError(err.message)
      },
    })

  const { mutate, isPending } = useUploadI9DocumentMutation(userId, {
    onSuccess: (response) => {
      toast.success('Documents uploaded successfully!', {
        position: 'top-center',
      })

      const isSupplementB = form.getValues('isSupplementB')
      if (isSupplementB && supplementBRef.current) {
        const supplementBData = supplementBRef.current.getValues()
        const userDocumentId = response.data?.createdDocuments?.[0]?.id
        submitSupplementB({
          ...supplementBData,
          reverifyUserDocumentId: userDocumentId,
        })
      } else {
        onUploadSuccess?.()
      }

      queryClient.invalidateQueries({ queryKey: userKey })
    },
    onError: (uploadError) => {
      console.error('I-9 Document Upload Error:', uploadError)
      setError(uploadError.message)
    },
  })

  const {
    fields: documents,
    append: onAddDocument,
    remove: onRemoveDocument,
  } = useFieldArray({
    name: 'documents',
    control: form.control,
  })

  const handleUploadSubmit = async (data: FormData) => {
    if (data.isSupplementB && supplementBRef.current) {
      const isValid = await supplementBRef.current.trigger()
      if (!isValid) return
    }
    mutate(data)
  }

  const handleAddDocument = (data: ListDocument) => {
    onAddDocument(data)
    if (layout === 'modal') {
      setIsModalOpen(false)
    }
  }

  const documentUploadForm = (
    <DocumentUpload documents={documents} onAddDocument={handleAddDocument} />
  )

  return (
    <form onSubmit={form.handleSubmit(handleUploadSubmit)}>
      {layout === 'inline' ? (
        <Box
          mt={2}
          p={3}
          backgroundColor="white"
          borderRadius="4px"
          border="1px solid #ccc"
        >
          {documentUploadForm}
        </Box>
      ) : (
        <>
          <Modal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title="Upload Document"
          >
            {documentUploadForm}
          </Modal>
          <Button
            block
            variant={form.formState.isDirty ? 'secondary' : 'primary'}
            onClick={() => setIsModalOpen(true)}
            my={3}
          >
            Add Document
          </Button>
        </>
      )}

      {documents.map((document, index) => (
        <Box
          key={document.documentType}
          mt={2}
          p={2}
          backgroundColor="white"
          borderRadius="4px"
          border="1px solid #ccc"
        >
          <Table
            variant="borderless"
            data={transformData(document)}
            columns={COLUMNS}
          />
          <Flex flexDirection="row" gap={2}>
            {document.frontImage ? (
              <img
                src={
                  typeof document.frontImage === 'string'
                    ? document.frontImage
                    : URL.createObjectURL(document.frontImage)
                }
                alt="Front Image"
                width={100}
                height={100}
                style={{ display: 'block', margin: '8px 0' }}
              />
            ) : null}
            {document.backImage ? (
              <img
                src={
                  typeof document.backImage === 'string'
                    ? document.backImage
                    : URL.createObjectURL(document.backImage)
                }
                alt="Back Image"
                width={100}
                height={100}
                style={{ display: 'block', margin: '8px 0' }}
              />
            ) : null}
          </Flex>
          <Button
            variant="secondary"
            block
            size="sm"
            onClick={() => onRemoveDocument(index)}
          >
            Remove
          </Button>
        </Box>
      ))}

      {form.formState.isDirty ? (
        <Box my={3}>
          <Controller
            name="isSupplementB"
            control={form.control}
            render={({ field }) => <Checkbox {...field}>Supplement B</Checkbox>}
          />
          {form.watch('isSupplementB') && (
            <SupplementBForm
              key={documents.map((document) => document.documentType).join(',')}
              ref={supplementBRef}
              documents={documents}
            />
          )}
        </Box>
      ) : null}

      {form.formState.isValid ? (
        <Button
          width={150}
          loading={isPending || isSubmitSupplementBPending}
          disabled={!form.formState.isValid}
          type="submit"
          block
          error={error}
        >
          Upload Documents
        </Button>
      ) : null}
    </form>
  )
}
